import { useEffect } from 'react';
import { useDatasources, useDashboardMutations } from '../../../../hooks';
import { Datasources } from '../../../../models/DatasourcesModels';
import { DataMutations } from '../../../../models/DatamutationsModels';
import { useTemplateStore } from './TemplateProvider';
import { isDebugMode } from '../utils/isDebugMode';
import isEmpty from 'lodash/isEmpty';
import { logger } from '../../../../utils';
import { DashboardJson } from '../../../models';
import { ViewRenderer } from '..';
import { DashboardLoading } from '../../componentVariations/loading';
import { getFlagValue } from '@appd/fso-ui-shell/feature-flags';

export const TemplateViewRenderer = () => {
  const [view] = useTemplateStore(store => (store as Record<string, unknown>).view);
  const [_dbname] = useTemplateStore(store => (store as Record<string, unknown>)._dbname);
  const [localTemplates] = useTemplateStore(
    store => (store as Record<string, unknown>).localTemplates,
  ) as ReadonlyArray<DashboardJson>;
  const [importedTemplates] = useTemplateStore(
    store => (store as Record<string, unknown>).importedTemplates,
  ) as ReadonlyArray<DashboardJson>;

  logger.log('TemplateViewRenderer', { _dbname, view });
  const [datasources, setDatasources, setStatesFromQueryResults] = useTemplateStore(
    store => (store as Record<string, unknown>).datasources,
  );
  const { isLoading, hasError, data } = useDatasources(datasources as Datasources, 'root');
  const [mutations, setMutations] = useTemplateStore(store => (store as Record<string, unknown>).datamutations);
  const { isMutationPending, hasMutationError, mutationData } = useDashboardMutations(
    mutations as DataMutations,
    'root',
  );

  useEffect(() => {
    (Object.entries(data || {}) as [string, Datasources][]).forEach(([key, result]) => {
      if (result != null && result.isSuccess && result.data != null) {
        setDatasources(`/data/${key}`, result);
      }
    });
  }, [data, setDatasources]);

  useEffect(() => {
    (Object.entries(mutationData || {}) as [string, DataMutations][]).forEach(([key, result]) => {
      // We only check for isIdle state as it indicates that the mutation function is ready to be mutated
      // isSuccess and isError states will not be populated here and if used can cause infinite loops
      if (result != null && result.isIdle) {
        setMutations(`/mutationData/${key}`, result);
      }
    });
  }, [mutationData, setMutations]);

  useEffect(() => {
    logger.log('storing queryResultStatuses', { isLoading, hasError });
    setStatesFromQueryResults({ isLoading, hasError });
  }, [isLoading, hasError, setStatesFromQueryResults]);

  const isTemporaryLoadingBarEnabled = getFlagValue('dashify-feature-flags-store', 'ENABLE_TEMPORARY_DS_LOADING_BAR');
  if (isLoading && isTemporaryLoadingBarEnabled) {
    return <DashboardLoading />;
  }

  if (!isEmpty(datasources) && isDebugMode() && hasError) {
    return <div>something went wrong while processing datasources</div>;
  }

  if (!isEmpty(mutations) && hasMutationError) {
    // This will be displayed in case the mutation function returns an error.
    // If removed, in case of error there will not be any change in the UI.
    // TODO: Mutation states will be handled similar to setStatesFromQueryResults()
    return <div>something went wrong while processing mutations</div>;
  }

  return (
    <ViewRenderer
      localTemplates={localTemplates as unknown as ReadonlyArray<DashboardJson>}
      importedTemplates={importedTemplates as unknown as ReadonlyArray<DashboardJson>}
      view={view}
      name={_dbname as string}
    />
  );
};
